import { useEffect, useState, useCallback } from "react";

interface CatalogProduct {
  article: number; article_pn: string; name: string; full_name: string;
  brand: string; category_id: number; price1: number; price2: number;
  qty_110: number; qty_markdown: number; price_markdown: number;
  warranty: string; is_new: boolean; last_stock_sync_at: string;
}
interface SyncStatus {
  total_products: number; in_stock_110: number;
  syncing_products: boolean; syncing_stock: boolean; last_stock_sync: string | null;
}
interface Filters {
  brands: { brand: string; count: number }[];
  categories: { id: number; name: string; count: number }[];
}

const token = () => localStorage.getItem("token") ?? "";
const h = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (p: number) => p ? Math.round(p).toLocaleString("ru-RU") + " ₸" : "—";
const fmtDate = (d: string | null) => d
  ? new Date(d).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  : "—";

export default function AlStyleCatalogPage() {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [filterData, setFilterData] = useState<Filters>({ brands: [], categories: [] });
  const [syncMsg, setSyncMsg] = useState("");

  // Filters state
  const [search, setSearch] = useState("");
  const [inStock, setInStock] = useState(false);
  const [brand, setBrand] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [isNew, setIsNew] = useState(false);

  const limit = 50;

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/sborka/sync/status", { headers: h() });
      if (r.ok) setStatus(await r.json());
    } catch {}
  }, []);

  const fetchFilters = useCallback(async (currentBrand?: string, currentCategoryId?: string) => {
    try {
      const q = new URLSearchParams();
      if (currentBrand) q.set('brand', currentBrand);
      if (currentCategoryId) q.set('categoryId', currentCategoryId);
      const r = await fetch(`/api/admin/sborka/catalog/filters?${q}`, { headers: h() });
      if (r.ok) setFilterData(await r.json());
    } catch {}
  }, []);

  const fetchProducts = useCallback(async (off: number, params: Record<string, string>) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ ...params, limit: String(limit), offset: String(off) });
      const r = await fetch(`/api/admin/sborka/catalog?${q}`, { headers: h() });
      if (r.ok) { const d = await r.json(); setProducts(d.items); setTotal(d.total); }
    } finally { setLoading(false); }
  }, []);

  const getParams = useCallback(() => {
    const p: Record<string, string> = {};
    if (search) p.search = search;
    if (inStock) p.inStock = "1";
    if (brand) p.brand = brand;
    if (categoryId) p.categoryId = categoryId;
    if (priceMin) p.priceMin = priceMin;
    if (priceMax) p.priceMax = priceMax;
    if (isNew) p.isNew = "1";
    return p;
  }, [search, inStock, brand, categoryId, priceMin, priceMax, isNew]);

  useEffect(() => {
    fetchStatus();
    fetchFilters();
    fetchProducts(0, {});
    const iv = setInterval(fetchStatus, 10000);
    return () => clearInterval(iv);
  }, [fetchStatus, fetchFilters, fetchProducts]);

  // Re-fetch filters when brand or category changes (cascading)
  useEffect(() => { fetchFilters(brand, categoryId); }, [brand, categoryId, fetchFilters]);

  // Debounced re-fetch on filter change
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); fetchProducts(0, getParams()); }, 400);
    return () => clearTimeout(t);
  }, [search, inStock, brand, categoryId, priceMin, priceMax, isNew, getParams, fetchProducts]);

  function changePage(dir: number) {
    const next = offset + dir * limit;
    if (next < 0 || next >= total) return;
    setOffset(next);
    fetchProducts(next, getParams());
  }

  function resetFilters() {
    setSearch(""); setInStock(false); setBrand(""); setCategoryId("");
    setPriceMin(""); setPriceMax(""); setIsNew(false);
  }

  async function triggerSync(type: "products" | "stock") {
    setSyncMsg("Запускаю...");
    try {
      const r = await fetch(`/api/admin/sborka/sync/${type}`, { method: "POST", headers: h() });
      const d = await r.json();
      setSyncMsg(d.status === "already_running" ? "Уже выполняется" : "Запущено ✓");
      setTimeout(() => setSyncMsg(""), 3000);
      fetchStatus();
    } catch { setSyncMsg("Ошибка"); }
  }

  const hasFilters = !!(search || inStock || brand || categoryId || priceMin || priceMax || isNew);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "16px 20px", boxSizing: "border-box", overflow: "hidden" }}>

      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexShrink: 0, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Каталог Al-Style (склад 110)</h1>
          {status && (
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-muted)", flexWrap: "wrap" }}>
              <span>Всего: <strong style={{ color: "var(--text)" }}>{status.total_products.toLocaleString("ru-RU")}</strong></span>
              <span>В наличии: <strong style={{ color: "#22c55e" }}>{status.in_stock_110.toLocaleString("ru-RU")}</strong></span>
              <span>Синхр. остатков: <strong style={{ color: "var(--text)" }}>{fmtDate(status.last_stock_sync)}</strong></span>
              {(status.syncing_products || status.syncing_stock) && <span style={{ color: "#f59e0b", fontWeight: 600 }}>● Синхронизация...</span>}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => triggerSync("products")} style={btn("#6366f1")}>🔄 Синхр. товары</button>
          <button onClick={() => triggerSync("stock")} style={btn("#0ea5e9")}>📦 Синхр. остатки</button>
          {syncMsg && <span style={{ fontSize: 12, color: "#f59e0b" }}>{syncMsg}</span>}
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
        padding: "12px 14px", marginBottom: 10, flexShrink: 0,
      }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>

          {/* Search */}
          <div style={{ flex: 2, minWidth: 180 }}>
            <Label>Поиск</Label>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Название, бренд, артикул..."
              style={inputStyle} />
          </div>

          {/* Brand */}
          <div style={{ flex: 1, minWidth: 130 }}>
            <Label>Бренд</Label>
            <select value={brand} onChange={e => setBrand(e.target.value)} style={inputStyle}>
              <option value="">Все бренды</option>
              {filterData.brands.map(b => (
                <option key={b.brand} value={b.brand}>{b.brand} ({b.count})</option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div style={{ flex: 1, minWidth: 150 }}>
            <Label>Категория</Label>
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={inputStyle}>
              <option value="">Все категории</option>
              {filterData.categories.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.count})</option>
              ))}
            </select>
          </div>

          {/* Price range */}
          <div style={{ minWidth: 90 }}>
            <Label>Цена от, ₸</Label>
            <input value={priceMin} onChange={e => setPriceMin(e.target.value)}
              placeholder="0" type="number" style={inputStyle} />
          </div>
          <div style={{ minWidth: 90 }}>
            <Label>Цена до, ₸</Label>
            <input value={priceMax} onChange={e => setPriceMax(e.target.value)}
              placeholder="∞" type="number" style={inputStyle} />
          </div>

          {/* Toggle buttons */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 1 }}>
            <button
              onClick={() => setInStock(v => !v)}
              style={toggleBtn(inStock, "#22c55e")}
            >
              {inStock ? "✓" : ""} В наличии
            </button>
            <button
              onClick={() => setIsNew(v => !v)}
              style={toggleBtn(isNew, "#a855f7")}
            >
              {isNew ? "✓" : ""} Новинки
            </button>
          </div>

          {/* Reset */}
          {hasFilters && (
            <button onClick={resetFilters} style={{ ...btn("#6b7280"), alignSelf: "flex-end" }}>✕ Сбросить</button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6, flexShrink: 0 }}>
        {loading ? "Загрузка..." : `Найдено: ${total.toLocaleString("ru-RU")} товаров`}
        {hasFilters && <span style={{ marginLeft: 8, color: "#6366f1" }}>● фильтры активны</span>}
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: "auto", background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)", minHeight: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ position: "sticky", top: 0, background: "var(--surface)", zIndex: 1 }}>
            <tr style={{ borderBottom: "2px solid var(--border)", color: "var(--text-muted)" }}>
              {[
                { l: "Артикул", w: 110 }, { l: "Название", w: "auto" },
                { l: "Бренд", w: 100 }, { l: "Закуп.", w: 100 }, { l: "Продажа", w: 110 },
                { l: "Наличие 110", w: 95 }, { l: "Уценка", w: 80 }, { l: "Цена уц.", w: 95 },
                { l: "Гарантия", w: 100 }, { l: "New", w: 50 }, { l: "Синхр.", w: 85 },
              ].map(col => (
                <th key={col.l} style={{ padding: "9px 10px", textAlign: "left", fontWeight: 600, whiteSpace: "nowrap", width: col.w, fontSize: 12 }}>{col.l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>Загрузка...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={11} style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>Ничего не найдено</td></tr>
            ) : products.map(p => (
              <tr key={p.article} style={{ borderBottom: "1px solid var(--border)" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--surface2)")}
                onMouseLeave={e => (e.currentTarget.style.background = "")}>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{p.article}</div>
                  {p.article_pn && <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{p.article_pn}</div>}
                </td>
                <td style={{ padding: "7px 10px", maxWidth: 320 }}>
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.full_name || p.name}>
                    {p.full_name || p.name}
                  </div>
                  {p.full_name && p.name !== p.full_name && (
                    <div style={{ fontSize: 10, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                  )}
                </td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap", fontSize: 12 }}>{p.brand || "—"}</td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap", color: "var(--text-muted)", fontSize: 12 }}>{fmt(p.price1)}</td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap", fontWeight: 700 }}>{fmt(p.price2)}</td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                  <span style={{
                    background: p.qty_110 > 0 ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.1)",
                    color: p.qty_110 > 0 ? "#22c55e" : "#ef4444",
                    padding: "2px 7px", borderRadius: 5, fontWeight: 600, fontSize: 12,
                  }}>{p.qty_110 > 0 ? `${p.qty_110} шт` : "нет"}</span>
                </td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap", color: p.qty_markdown > 0 ? "#f59e0b" : "var(--text-muted)", fontSize: 12 }}>
                  {p.qty_markdown > 0 ? `${p.qty_markdown} шт` : "—"}
                </td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap", color: "#f59e0b", fontSize: 12 }}>
                  {p.price_markdown ? fmt(p.price_markdown) : "—"}
                </td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap", color: "var(--text-muted)", fontSize: 12 }}>{p.warranty || "—"}</td>
                <td style={{ padding: "7px 10px", textAlign: "center", fontSize: 11 }}>
                  {p.is_new ? <span style={{ color: "#a855f7", fontWeight: 700 }}>NEW</span> : ""}
                </td>
                <td style={{ padding: "7px 10px", whiteSpace: "nowrap", fontSize: 11, color: "var(--text-muted)" }}>
                  {fmtDate(p.last_stock_sync_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", fontSize: 13, flexShrink: 0 }}>
        <button onClick={() => changePage(-1)} disabled={offset === 0} style={btn("#374151")}>← Назад</button>
        <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
          {total === 0 ? "0" : `${offset + 1}–${Math.min(offset + limit, total)}`} из {total.toLocaleString("ru-RU")}
        </span>
        <button onClick={() => changePage(1)} disabled={offset + limit >= total} style={btn("#374151")}>Вперёд →</button>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4, fontWeight: 500 }}>{children}</div>;
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "6px 10px", boxSizing: "border-box",
  background: "var(--bg)", border: "1px solid var(--border)",
  borderRadius: 7, color: "var(--text)", fontSize: 13,
};

function toggleBtn(active: boolean, color: string): React.CSSProperties {
  return {
    background: active ? color : "transparent",
    color: active ? "#fff" : "var(--text-muted)",
    border: `1px solid ${active ? color : "var(--border)"}`,
    borderRadius: 7, padding: "5px 12px", fontSize: 12, fontWeight: 600,
    cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s",
    minWidth: 100, textAlign: "left",
  };
}

function btn(bg: string): React.CSSProperties {
  return { background: bg, color: "#fff", border: "none", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
}
